import { Lightning } from '@lightningjs/sdk';

export default class CTAShader extends Lightning.shaders.WebGLDefaultShader {
  constructor(context) {
    super(context);
    this._radius = 0;
    this._borderWidth = 0;
    this._progress = 0;
    this._fc = 0x00ffffff;
    this._pc = 0x00ffffff;
    this._ibc = 0x00ffffff;
    this._obc = 0x00ffffff;
    this._fillColor = this._getNormalizedColor(this._fc);
    this._progressColor = this._getNormalizedColor(this._pc);
    this._outerBorderColor = this._getNormalizedColor(this._ibc);
    this._outerBorderColor = this._getNormalizedColor(this._obc);
  }

  set radius(v) {
    this._radius = v;
    this.redraw();
  }

  get radius() {
    return this._radius;
  }

  get progress() {
    return this._progress;
  }

  set progress(value) {
    this._progress = value;
    this.redraw();
  }

  set topLeft(num) {
    this._radius[0] = num;
    this.redraw();
  }

  get topLeft() {
    return this._radius[0];
  }

  set topRight(num) {
    this._radius[1] = num;
    this.redraw();
  }

  get topRight() {
    return this._radius[1];
  }

  set bottomRight(num) {
    this._radius[2] = num;
    this.redraw();
  }

  get bottomRight() {
    return this._radius[2];
  }

  set bottomLeft(num) {
    this._radius[3] = num;
    this.redraw();
  }

  get bottomLeft() {
    return this._radius[4];
  }

  get outerBorderColor() {
    return this._obc;
  }

  set outerBorderColor(argb) {
    this._obc = argb;
    this._outerBorderColor = this._getNormalizedColor(argb);
    this.redraw();
  }

  get innerBorderColor() {
    return this._ibc;
  }

  set innerBorderColor(argb) {
    this._ibc = argb;
    this._innerBorderColor = this._getNormalizedColor(argb);
    this.redraw();
  }

  get fillColor() {
    return this._fc;
  }

  set fillColor(argb) {
    this._fc = argb;
    this._fillColor = this._getNormalizedColor(argb);
    this.redraw();
  }

  get progressColor() {
    return this._pc;
  }

  set progressColor(argb) {
    this._pc = argb;
    this._progressColor = this._getNormalizedColor(argb);
    this.redraw();
  }

  get borderWidth() {
    return this._borderWidth;
  }

  set borderWidth(num) {
    this._borderWidth = num;
    this.redraw();
  }

  _getNormalizedColor(color) {
    const col = Lightning.StageUtils.getRgbaComponentsNormalized(color);
    col[0] *= col[3];
    col[1] *= col[3];
    col[2] *= col[3];

    return new Float32Array(col);
  }

  setupUniforms(operation) {
    super.setupUniforms(operation);
    const owner = operation.shaderOwner;
    const renderPrecision = this.ctx.stage.getRenderPrecision();
    this._setUniform('radius', this._radius * renderPrecision, this.gl.uniform1f);
    this._setUniform('scale', 1, this.gl.uniform1f);
    this._setUniform('progress', this._progress, this.gl.uniform1f);
    this._setUniform('borderWidth', this._borderWidth * renderPrecision, this.gl.uniform1f);
    this._setUniform('fillColor', this._fillColor, this.gl.uniform4fv);
    this._setUniform('progressColor', this._progressColor, this.gl.uniform4fv);
    this._setUniform('innerBorderColor', this._innerBorderColor, this.gl.uniform4fv);
    this._setUniform('outerBorderColor', this._outerBorderColor, this.gl.uniform4fv);
    this._setUniform(
      'resolution',
      new Float32Array([owner._w * renderPrecision, owner._h * renderPrecision]),
      this.gl.uniform2fv,
    );
  }
}

CTAShader.vertexShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif
    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;

    uniform vec2 projection;
    varying vec2 vTextureCoord;

    void main(void){
        gl_Position = vec4(aVertexPosition.x * projection.x - 1.0, aVertexPosition.y * -abs(projection.y) + 1.0, 0.0, 1.0);
        vTextureCoord = aTextureCoord;
        gl_Position.y = -sign(projection.y) * gl_Position.y;
    }
`;

CTAShader.fragmentShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    varying vec2 vTextureCoord;

    uniform float radius;
    uniform float borderWidth;
    uniform float scale;
    uniform float progress;
    uniform float imageMaskFactor;
    uniform vec2 resolution;
    uniform vec4 fillColor;
    uniform vec4 progressColor;
    uniform vec4 innerBorderColor;
    uniform vec4 outerBorderColor;

    float boxDist(vec2 p, vec2 size, float radius) {
      size -= vec2(radius);
      vec2 d = abs(p) - size;

      return min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - radius;
    }

    float fillMask(float dist) {
        return clamp(-dist, 0.0, 1.0);
    }

    vec2 texCoordsWithOffset(float offset) {
        vec2 texCoordsMultiplier = (1.0 + (offset * 2.0) / resolution);
        vec2 texCoordsOffset = offset / resolution;

        return vTextureCoord * texCoordsMultiplier - texCoordsOffset;
    }

    void main() {
      vec2 halfRes = resolution * 0.5;
      vec2 halfResOffset = halfRes + 0.5;
      vec2 texCoords = texCoordsWithOffset(borderWidth / scale);

      float imageDistance = boxDist(texCoords * resolution - halfRes, halfResOffset, radius / scale);
      float imageMask = fillMask(imageDistance);
      vec4 backgroundColor = fillColor;

      if (texCoords.x < progress) {
        backgroundColor.rgb = progressColor.rgb;
      }

      float outerRadius = (radius + borderWidth * 0.375) / scale;
      // NOTE: it should be exactly half of the border, but 0.45 simply looks better
      float borderInnerDistance = boxDist(texCoordsWithOffset((borderWidth * 0.45) / scale) * resolution - halfRes, halfResOffset, outerRadius);
      float borderOuterDistance = boxDist(vTextureCoord * resolution - halfRes, halfRes, outerRadius + borderWidth * 0.125);
      vec4 border = mix(outerBorderColor, innerBorderColor, fillMask(borderInnerDistance));
      border = mix(backgroundColor * imageMask, border, fillMask(borderOuterDistance) - imageMask);

      gl_FragColor = border;
    }
`;
